import type { CountryCode } from './types';
import { getCountries, getEventsForReligionByCountry, getEventsForNatureByCountry, getMinEventsThreshold, getCountryByCode } from './data-source';
import { SITE_NAME } from './brand';
import { YEAR, getCountryNameEntry, getPhraseEntry, getTemplateStr, getLanguagesForCountry, getLangLabel, getLangLabelEn } from './i18n-helpers';
import { buildLocalizedSlug, buildLocalizedDisplay } from './slug-word-order';

export interface CountryLensSlugEntry {
  code: CountryCode;
  lensType: 'religion' | 'nature';
  lang: string;
  slug: string;
  pageTitle: string;
  pageDescription: string;
  h2Title: string;
}

function buildCountryLensSlug(code: CountryCode, lensType: 'religion' | 'nature', lang: string): string {
  const countryEntry = getCountryNameEntry(code, lang);
  const phraseKey = lensType === 'religion' ? 'religion_lens' : 'nature_lens';
  const phrase = getPhraseEntry(phraseKey, lang);
  return buildLocalizedSlug({ subject: countryEntry.slug, phrase: phrase.slug, year: String(YEAR) }, lang);
}

function buildCountryLensEntry(
  code: CountryCode,
  lensType: 'religion' | 'nature',
  lang: string,
  eventCount: number,
): CountryLensSlugEntry {
  const countryEntry = getCountryNameEntry(code, lang);
  const country = getCountryByCode(code);
  const flag = country?.flag ?? '';

  const phraseKey = lensType === 'religion' ? 'religion_lens' : 'nature_lens';
  const phrase = getPhraseEntry(phraseKey, lang);

  const descKey = lensType === 'religion' ? 'religion_lens_desc' : 'nature_lens_desc';
  const h2Key = lensType === 'religion' ? 'religion_lens_h2' : 'nature_lens_h2';

  const slug = buildCountryLensSlug(code, lensType, lang);

  const pageTitle = `${buildLocalizedDisplay({ subject: countryEntry.display, phrase: phrase.display, year: YEAR }, lang)} — ${SITE_NAME}`;

  return {
    code,
    lensType,
    lang,
    slug,
    pageTitle,
    pageDescription: getTemplateStr(descKey, lang, { country: countryEntry.display, count: eventCount, year: YEAR }),
    h2Title: getTemplateStr(h2Key, lang, { country: countryEntry.display, flag, year: YEAR }),
  };
}

// --- Country-lens registry cache ---

let _countryLensSlugCache: Record<string, { code: CountryCode; lensType: 'religion' | 'nature'; lang: string }> | null = null;
let _countryLensReverseCache: Record<string, string> | null = null;

export function ensureCountryLensBuilt() {
  if (_countryLensSlugCache) return;

  const countries = getCountries();
  const cache: Record<string, { code: CountryCode; lensType: 'religion' | 'nature'; lang: string }> = {};
  const reverse: Record<string, string> = {};

  for (const country of countries) {
    if (!getCountryNameEntry(country.code, 'en')) continue;

    for (const lensType of ['religion', 'nature'] as const) {
      const events = lensType === 'religion'
        ? getEventsForReligionByCountry(country.code)
        : getEventsForNatureByCountry(country.code);
      if (events.length < getMinEventsThreshold('country_lens')) continue;

      const langs = getLanguagesForCountry(country.code);
      for (const lang of langs) {
        const entry = buildCountryLensEntry(country.code, lensType, lang, events.length);
        let slug = entry.slug;
        if (cache[slug]) {
          slug = `${slug}-${lang}`;
        }
        cache[slug] = { code: country.code, lensType, lang };
        reverse[`${country.code}:${lensType}:${lang}`] = slug;
      }
    }
  }

  if (Object.keys(cache).length === 0) return;

  _countryLensSlugCache = cache;
  _countryLensReverseCache = reverse;
}

// --- Public API ---

export function getAllCountryLensSlugs(): string[] {
  ensureCountryLensBuilt();
  return Object.keys(_countryLensSlugCache ?? {});
}

export function getCountryLensSlugEntry(slug: string): CountryLensSlugEntry | null {
  ensureCountryLensBuilt();
  const match = _countryLensSlugCache?.[slug];
  if (!match) return null;

  const events = match.lensType === 'religion'
    ? getEventsForReligionByCountry(match.code)
    : getEventsForNatureByCountry(match.code);

  const entry = buildCountryLensEntry(match.code, match.lensType, match.lang, events.length);
  entry.slug = slug;
  return entry;
}

export function getAlternateCountryLensLanguages(
  code: CountryCode,
  lensType: 'religion' | 'nature',
): Array<{ lang: string; slug: string; label: string }> {
  ensureCountryLensBuilt();
  const langs = getLanguagesForCountry(code);
  return langs
    .map(lang => ({
      lang,
      slug: _countryLensReverseCache?.[`${code}:${lensType}:${lang}`] ?? '',
      label: getLangLabel(lang),
      labelEn: getLangLabelEn(lang),
    }))
    .filter(x => x.slug);
}

export function getCountryLensSlugForId(
  code: CountryCode,
  lensType: 'religion' | 'nature',
  lang: string,
): string {
  ensureCountryLensBuilt();
  return _countryLensReverseCache?.[`${code}:${lensType}:${lang}`]
    || _countryLensReverseCache?.[`${code}:${lensType}:en`]
    || '';
}

/** Internal: access the raw country-lens slug cache for cross-registry functions */
export function _getCountryLensSlugCache(): Record<string, { code: CountryCode; lensType: 'religion' | 'nature'; lang: string }> {
  ensureCountryLensBuilt();
  return _countryLensSlugCache ?? {};
}
